import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import SectionsPage from './pages/SectionsPage';
import BlogDetailPage from './pages/BlogDetailPage';
import FeesStructurePage from './pages/FeesStructurePage';
import PopupEnquiryModal from './components/common/PopupEnquiryModal';
import FloatingButtons from './components/common/FloatingButtons';
import { EnquiryProvider } from './context/EnquiryContext';

const POPUP_DELAY = 1000; // 1 second initial delay
const POPUP_REOPEN_DELAY = 15 * 60 * 1000; // 15 minutes

function App() {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquirySuccessData, setEnquirySuccessData] = useState(null);
  const reopenTimerRef = useRef(null);
  const pendingSuccessCallbackRef = useRef(null);
  const successAutoCloseTimerRef = useRef(null);

  const openEnquiry = useCallback((onSuccessCallback) => {
    setEnquiryOpen(true);
    setEnquirySuccessData(null);
    // Store the callback to be fired after form success + success view delay
    pendingSuccessCallbackRef.current = onSuccessCallback || null;
    // Clear any pending reopen timer (user initiated, so postpone auto-reopen)
    if (reopenTimerRef.current) {
      clearTimeout(reopenTimerRef.current);
      reopenTimerRef.current = null;
    }
  }, []);

  const closeEnquiry = useCallback(() => {
    setEnquiryOpen(false);
    setEnquirySuccessData(null);
    // Clear the pending success callback when manually closed
    pendingSuccessCallbackRef.current = null;
    // Clear any auto-close timer
    if (successAutoCloseTimerRef.current) {
      clearTimeout(successAutoCloseTimerRef.current);
      successAutoCloseTimerRef.current = null;
    }
    // Reopen the popup automatically after 15 minutes
    if (reopenTimerRef.current) {
      clearTimeout(reopenTimerRef.current);
    }
    reopenTimerRef.current = setTimeout(() => {
      setEnquiryOpen(true);
    }, POPUP_REOPEN_DELAY);
  }, []);

  const handleFormSubmitted = useCallback((values) => {
    // Show success view first (already handled by PopupEnquiryModal via onFormSubmitted)
    // Wait 2.5 seconds, then close popup, restart reopen timer, and fire the pending callback
    successAutoCloseTimerRef.current = setTimeout(() => {
      setEnquiryOpen(false);
      setEnquirySuccessData(null);
      // Reopen the popup automatically after 15 minutes
      if (reopenTimerRef.current) {
        clearTimeout(reopenTimerRef.current);
      }
      reopenTimerRef.current = setTimeout(() => {
        setEnquiryOpen(true);
      }, POPUP_REOPEN_DELAY);
      // Fire the pending success callback after the success view closes
      if (pendingSuccessCallbackRef.current) {
        pendingSuccessCallbackRef.current(values);
        pendingSuccessCallbackRef.current = null;
      }
    }, 2500);
  }, []);

  const showEnquirySuccess = useCallback((values) => {
    setEnquirySuccessData(values);
    setEnquiryOpen(true);
    // Clear any pending reopen timer when showing success
    if (reopenTimerRef.current) {
      clearTimeout(reopenTimerRef.current);
      reopenTimerRef.current = null;
    }
  }, []);

  // Show popup automatically after initial delay
  useEffect(() => {
    const initialTimer = setTimeout(() => {
      setEnquiryOpen(true);
    }, POPUP_DELAY);

    return () => {
      clearTimeout(initialTimer);
      if (reopenTimerRef.current) {
        clearTimeout(reopenTimerRef.current);
      }
    };
  }, []);

  const enquiryContextValue = {
    showEnquirySuccess,
    openEnquiry,
  };

  return (
    <EnquiryProvider value={enquiryContextValue}>
      <Router>
        <PopupEnquiryModal
          open={enquiryOpen}
          onClose={closeEnquiry}
          initialSuccessData={enquirySuccessData}
          onFormSubmitted={handleFormSubmitted}
        />
        <FloatingButtons />
        <Header onEnquiryOpen={openEnquiry} />
        <Routes>
          <Route path="/" element={<SectionsPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/fees-structure" element={<FeesStructurePage />} />
        </Routes>
        <Footer />
      </Router>
    </EnquiryProvider>
  );
}

export default App;